// Expenses Management Component
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Alert,
  Card,
  CardContent,
  Select,
  MenuItem as SelectMenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";

import {
  Add,
  Search,
  Edit,
  Delete,
  Receipt,
  Category,
  CalendarMonth,
} from "@mui/icons-material";
import { DataTable } from "../common";
import { useCurrency } from "../../contexts/CurrencyContext";
import apiService from "../../services/api";
import authService from "../../services/auth";



const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);

  const [dateFilter, setDateFilter] = useState("current_month");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [userPermissions, setUserPermissions] = useState(null);
  const [customDateRange, setCustomDateRange] = useState({ start: "", end: "" });
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

  // Use currency context for proper currency formatting
  const { formatPrice, currencySymbol } = useCurrency();

  const isAdminLike = (user) => String(user?.role_name || "").toLowerCase().includes("admin");

  const hasPermission = (module, action = "view") => {
    const user = authService.getUser();
    if (isAdminLike(user)) return true;
    if (!userPermissions) return false;
    return userPermissions?.[module]?.[action] === true;
  };

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    amount: "",
    category: "",
    expense_date: "",
    payment_method: "",
  });

  const categories = [
    "Rent",
    "Utilities",
    "Supplies",
    "Equipment",
    "Marketing",
    "Insurance",
    "Professional Services",
    "Maintenance",
    "Staff",
    "Other",
  ];

  const paymentMethods = [
    "Cash",
    "Credit Card",
    "Debit Card",
    "Bank Transfer",
    "Cheque",
    "Other",
    "UP (Gpay, PhonePe, etc)",
  ];

  // DataTable columns configuration
  const columns = [
    {
      field: 'title',
      header: 'Expense',
      type: 'text',
      sortable: true,
      exportable: true,
      exportWidth: 30,
    },
    {
      field: 'category',
      header: 'Category',
      type: 'status',
      sortable: true,
      exportable: true,
      exportWidth: 15,
    },
    {
      field: 'expense_date',
      header: 'Date',
      type: 'date',
      sortable: true,
      exportable: true,
      exportWidth: 15,
    },
    {
      field: 'amount',
      header: 'Amount',
      type: 'amount',
      sortable: true,
      exportable: true,
      exportWidth: 15,
      align: 'right',
    },
    {
      field: 'payment_method',
      header: 'Payment Method',
      type: 'text',
      sortable: true,
      exportable: true,
      exportWidth: 20,
    },
    {
      field: 'actions',
      header: 'Actions',
      type: 'actions',
      sortable: false,
      exportable: false,
    },
  ];

  // DataTable actions configuration
  const actions = (() => {
    const acts = [];
    if (hasPermission("expenses", "edit")) {
      acts.push({
        type: "edit",
        label: "Edit",
        icon: <Edit sx={{ mr: 1 }} />,
      });
    }
    if (hasPermission("expenses", "delete")) {
      acts.push({
        type: "delete",
        label: "Delete",
        icon: <Delete sx={{ mr: 1 }} />,
      });
    }
    return acts;
  })();

  // Status options for category display
  const statusOptions = categories.map(category => ({
    value: category,
    label: category,
    color: 'primary',
  }));

  useEffect(() => {
    fetchExpenses();
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    try {
      const user = authService.getUser();
      if (!user?.id) return;
      const response = await apiService.get(`/roles/user/${user.id}/permissions`);
      setUserPermissions(response.data || {});
    } catch (e) {
      // safest fallback: no permissions (so no edit/delete)
      setUserPermissions({});
    }
  };

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      const response = await apiService.get("/expenses");
      if (response.success) {
        setExpenses(response.data.expenses || []);
      } else {
        setError("Failed to fetch expenses");
      }
    } catch (err) {
      setError("Error fetching expenses: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const getFilteredExpenses = () => {
    let filtered = (expenses || []).filter(
      (expense) =>
        (expense.title && expense.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (expense.category && expense.category.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (expense.description && expense.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Filter by category
    if (categoryFilter !== "all") {
      filtered = filtered.filter(
        (expense) => expense.category === categoryFilter
      );
    }

    // Filter by date
    const now = new Date();
    let startDate = new Date();
    let endDate = new Date();

    switch (dateFilter) {
      case "current_month":
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case "last_month":
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case "last_quarter":
        startDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case "this_year":
        startDate = new Date(now.getFullYear(), 0, 1);
        endDate = new Date(now.getFullYear(), 11, 31);
        break;
      case "custom":
        if (customDateRange.start && customDateRange.end) {
          startDate = new Date(customDateRange.start);
          endDate = new Date(customDateRange.end);
          
          // Validate that end date is greater than start date
          if (endDate <= startDate) {
            return { isValid: false, error: "End date must be greater than start date" };
          }
          
          // Validate custom date range (max 1 year)
          const diffTime = Math.abs(endDate - startDate);
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          if (diffDays > 365) {
            return { isValid: false, error: "Custom date range cannot exceed 1 year" };
          }
          
          // Validate that dates are not in the future
          const today = new Date();
          today.setHours(23, 59, 59, 999); // Set to end of today
          if (startDate > today || endDate > today) {
            return { isValid: false, error: "Dates cannot be in the future" };
          }
        }
        break;
      default:
        break;
    }

    if (dateFilter !== "all") {
      filtered = filtered.filter(
        (expense) => {
          const expenseDate = new Date(expense.expense_date);
          return expenseDate >= startDate && expenseDate <= endDate;
        }
      );
    }

    return { isValid: true, data: filtered.sort(
      (a, b) => new Date(b.expense_date) - new Date(a.expense_date)
    ) };
  };

  const handleOpenDialog = (expense = null) => {
    // Clear previous errors
    setError("");
    setFieldErrors({});
    
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        title: expense.title,
        description: expense.description || "",
        amount: expense.amount,
        category: expense.category,
        expense_date: expense.expense_date,
        payment_method: expense.payment_method || "",
      });
    } else {
      setEditingExpense(null);
      const today = new Date().toISOString().split("T")[0];
      setFormData({
        title: "",
        description: "",
        amount: "",
        category: "",
        expense_date: today,
        payment_method: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingExpense(null);
    setError("");
    setFieldErrors({});
  };

  const handleInputChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
    
    // Clear field error when user starts typing
    if (fieldErrors[event.target.name]) {
      setFieldErrors(prev => ({
        ...prev,
        [event.target.name]: ""
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.title.trim()) {
      errors.title = "Expense title is required";
    }
    
    if (!formData.category) {
      errors.category = "Category is required";
    }
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      errors.amount = "Amount must be greater than 0";
    }
    
    if (!formData.expense_date) {
      errors.expense_date = "Expense date is required";
    } else {
      const selectedDate = new Date(formData.expense_date);
      const today = new Date();
      today.setHours(23, 59, 59, 999); // Set to end of today
      
      if (selectedDate > today) {
        errors.expense_date = "Expense date cannot be in the future";
      }
    }
    
    if (!formData.payment_method) {
      errors.payment_method = "Payment method is required";
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    // Clear previous errors
    setError("");
    setFieldErrors({});

    // Permission checks (UI-level). API also enforces this.
    if (editingExpense && !hasPermission("expenses", "edit")) {
      setError("Permission denied");
      return;
    }
    if (!editingExpense && !hasPermission("expenses", "add")) {
      setError("Permission denied");
      return;
    }
    
    // Validate form
    if (!validateForm()) {
      setError("Please fix the errors below");
      return;
    }
    
    try {
      // Prepare data for submission
      const submitData = {
        ...formData
      };
      
      let response;
      if (editingExpense) {
        response = await apiService.put(
          `/expenses/${editingExpense.id}`,
          submitData
        );
      } else {
        response = await apiService.post("/expenses", submitData);
      }

      if (response.success) {
        fetchExpenses();
        handleCloseDialog();
      } else {
        setError(response.message || "Failed to save expense");
      }
    } catch (err) {
      setError("Error saving expense: " + err.message);
    }
  };

  const handleDeleteExpense = async (expense) => {
    // Permission checks (UI-level). API also enforces this.
    if (!hasPermission("expenses", "delete")) {
      setError("Permission denied");
      return;
    }

    const exp = expense || selectedExpense;
    if (exp) {
      try {
        const response = await apiService.delete(
          `/expenses/${exp.id}`
        );
        if (response.success) {
          fetchExpenses();
        } else {
          setError("Failed to delete expense");
        }
      } catch (err) {
        setError("Error deleting expense: " + err.message);
      }
    }
    setSelectedExpense(null);
  };

  // DataTable action handler
  const handleTableAction = (actionType, expense) => {
    switch (actionType) {
      case 'edit':
        handleOpenDialog(expense);
        break;
      case 'delete':
        handleDeleteExpense(expense);
        break;
      default:
        console.log('Unknown action:', actionType);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
  };

  const calculateTotalExpenses = () => {
    return filteredExpenses.reduce((total, expense) => {
      return total + parseFloat(expense.amount);
    }, 0);
  };

  const filteredExpensesResult = getFilteredExpenses();
  
  // Handle validation errors from date filtering
  useEffect(() => {
    if (!filteredExpensesResult.isValid) {
      setError(filteredExpensesResult.error);
    } else {
      setError(""); // Clear any previous errors
    }
  }, [filteredExpensesResult]);
  
  const filteredExpenses = filteredExpensesResult.isValid ? filteredExpensesResult.data : [];

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <Typography>Loading expenses...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight="bold">
          Expenses
        </Typography>
        <Box display="flex" gap={2}>
          {/* <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchExpenses}
          >
            Refresh
          </Button> */}
          {hasPermission("expenses", "add") && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()} 
            >
              Add
            </Button>
          )}
        </Box>
      </Box>



      {/* Date Range Validation Error */}
      {!filteredExpensesResult.isValid && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="bold">
            Date Range Issue: {filteredExpensesResult.error}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Please adjust your date selection. Custom date ranges cannot exceed 1 year and cannot include future dates.
          </Typography>
        </Alert>
      )}

      {/* Summary Cards */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { 
          xs: 'repeat(2, 1fr)', 
          sm: 'repeat(4, 1fr)' 
        }, 
        gap: 3, 
        mb: 4 
      }}>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <Typography sx={{ fontSize: 40, color: 'primary.main', mr: 2 }}>
                {currencySymbol}
              </Typography>
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  Total
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatPrice(calculateTotalExpenses())}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <Receipt sx={{ fontSize: 40, color: 'secondary.main', mr: 2 }} />
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  Total Count
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {filteredExpenses.length}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <Category sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  Categories
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {categories.length}
                </Typography>
              </Box>
            </Box>
          </CardContent>  
        </Card>
        <Card>
          <CardContent>
            <Box display="flex" alignItems="center">
              <CalendarMonth sx={{ fontSize: 40, color: 'success.main', mr: 2 }} />
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  This Month
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatPrice(calculateTotalExpenses())}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ flex: 2, minWidth: 300 }}>
              <TextField
                fullWidth
                placeholder="Search expenses..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <SelectMenuItem value="all">All Categories</SelectMenuItem>
                  {categories.map((category) => (
                    <SelectMenuItem key={category} value={category}>
                      {category}
                    </SelectMenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateFilter}
                  label="Date Range"
                  onChange={(e) => {
                    setDateFilter(e.target.value);
                    if (e.target.value === "custom") {
                      setShowCustomDatePicker(true);
                    } else {
                      setShowCustomDatePicker(false);
                      setCustomDateRange({ start: "", end: "" });
                    }
                  }}
                >
                  <SelectMenuItem value="current_month">Current Month</SelectMenuItem>
                  <SelectMenuItem value="last_month">Last Month</SelectMenuItem>
                  <SelectMenuItem value="last_quarter">Last 3 Months</SelectMenuItem>
                  <SelectMenuItem value="this_year">This Year</SelectMenuItem>
                  <SelectMenuItem value="custom">Custom Range</SelectMenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
          
          {/* Custom Date Range Picker */}
          {showCustomDatePicker && (
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
              <Typography variant="subtitle2" gutterBottom>
                Select Custom Date Range (Max 1 Year)
              </Typography>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={customDateRange.start}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max: new Date().toISOString().split('T')[0] // Prevent future dates
                  }}
                />
                <Typography variant="body2">to</Typography>
                <TextField
                  label="End Date"
                  type="date"
                  value={customDateRange.end}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: customDateRange.start || undefined, // Must be after start date
                    max: new Date().toISOString().split('T')[0] // Prevent future dates
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setCustomDateRange({ start: "", end: "" });
                    setShowCustomDatePicker(false);
                    setDateFilter("current_month");
                  }}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* DataTable */}
      <DataTable
        data={filteredExpenses}
        columns={columns}
        actions={actions}
        onAction={handleTableAction}
        itemsPerPage={50}
        showPagination={true}
        showExport={hasPermission("expenses", "export")}
        exportFileName="expenses"
        formatPrice={formatPrice}
        formatDate={formatDate}
        statusOptions={statusOptions}
        getStatusColor={() => 'primary'}
        emptyStateIcon={Receipt}
        emptyStateTitle="No expenses yet"
        emptyStateSubtitle="Add your first expense to get started"
        searchEmptyStateTitle="No expenses found matching your search"
        searchEmptyStateSubtitle="Try adjusting your search terms"
        hasSearchResults={!!searchTerm}
      />

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={(event, reason) => {
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            handleCloseDialog();
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingExpense ? "Edit Expense" : "Add New Expense"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2, color: "error.main" }} onClose={() => setError("")}>
                {error}
              </Alert>
            )}
            
            {/* Row 1: Expense Title (full width) */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Expense Title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                error={!!fieldErrors.title}
                helperText={fieldErrors.title}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-error": {
                      borderColor: "error.main",
                    },
                  },
                }}
              />
            </Box>

            {/* Row 2: Category + Amount */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <FormControl fullWidth required error={!!fieldErrors.category}>
                <InputLabel>Category</InputLabel>
                <Select
                  name="category"
                  value={formData.category}
                  label="Category"
                  onChange={handleInputChange}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-error": {
                        borderColor: "error.main",
                      },
                    },
                  }}
                >
                  {categories.map((category) => (
                    <SelectMenuItem key={category} value={category}>
                      {category}
                    </SelectMenuItem>
                  ))}
                </Select>
                {fieldErrors.category && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                    {fieldErrors.category}
                  </Typography>
                )}
              </FormControl>
              <TextField
                fullWidth
                label="Amount"
                name="amount"
                type="number"
                value={formData.amount}
                onChange={handleInputChange}
                required
                error={!!fieldErrors.amount}
                helperText={fieldErrors.amount}
                onWheel={(e) => e.target.blur()}
                onKeyDown={(e) => {
                  if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                    e.preventDefault();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">{currencySymbol}</InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-error": {
                      borderColor: "error.main",
                    },
                  },
                }}
              />
            </Box>

            {/* Row 3: Expense Date + Payment Method */}
            <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                label="Expense Date"
                name="expense_date"
                type="date"
                value={formData.expense_date}
                onChange={handleInputChange}
                required
                error={!!fieldErrors.expense_date}
                helperText={fieldErrors.expense_date}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  max: new Date().toISOString().split('T')[0] // Prevent future dates
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-error": {
                      borderColor: "error.main",
                    },
                  },
                }}
              />
              <FormControl fullWidth required error={!!fieldErrors.payment_method}>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  name="payment_method"
                  value={formData.payment_method}
                  label="Payment Method"
                  onChange={handleInputChange}
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "&.Mui-error": {
                        borderColor: "error.main",
                      },
                    },
                  }}
                >
                  {paymentMethods.map((method) => (
                    <SelectMenuItem key={method} value={method}>
                      {method}
                    </SelectMenuItem>
                  ))}
                </Select>
                {fieldErrors.payment_method && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                    {fieldErrors.payment_method}
                  </Typography>
                )}
              </FormControl>
            </Box>

            {/* Row 4: Description (full width) */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Description (Optional)"
                name="description"
                multiline
                rows={3}
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Additional details about this expense... (optional)"
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingExpense ? "Update" : "Add"} Expense
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Expenses;
